import { proxy } from 'valtio'
import axios from '../api/axios'

// Portal state — separate from regular user auth state
export const portalState = proxy({
    portalId: null,
    type: null,
    name: null,
    contactId: null,
    contactName: null,
    permissions: {},
    accessToken: null
})

export const usePortal = () => {

    const resolvePortal = async (token) => {
        try {
            const res = await axios.post('/portal/open', { token })
            return res.data
        } catch (err) {
            return { error: err.response?.data?.error || { name: 'error.unknown' } }
        }
    }

    const authenticatePortal = async (token, passcode = null) => {
        try {
            const res = await axios.post('/portal/auth', { token, passcode })
            const { accessToken, portalId, type, name, contactId, contactName, permissions } = res.data

            // Store in sessionStorage (cleared when the browser tab closes)
            sessionStorage.setItem('portalToken', accessToken)

            portalState.portalId    = portalId
            portalState.type        = type
            portalState.name        = name
            portalState.contactId   = contactId
            portalState.contactName = contactName
            portalState.permissions = permissions
            portalState.accessToken = accessToken

            return { success: true }
        } catch (err) {
            return { error: err.response?.data?.error || { name: 'error.unknown' } }
        }
    }

    // Re-hydrate in-memory state from sessionStorage on page refresh within the same tab
    const restorePortalSession = () => {
        const token = sessionStorage.getItem('portalToken')
        if (!token) return false
        try {
            const payload = JSON.parse(atob(token.split('.')[1]))
            if (payload.type !== 'portal') return false
            portalState.accessToken = token
            portalState.portalId    = payload.portalId
            portalState.type        = payload.portalType
            portalState.contactId   = payload.contactId
            portalState.permissions = payload.permissions
            portalState.name        = payload.name || null
            portalState.contactName = payload.contactName || null
            return true
        } catch {
            return false
        }
    }

    const logoutPortal = () => {
        sessionStorage.removeItem('portalToken')
        portalState.portalId    = null
        portalState.type        = null
        portalState.name        = null
        portalState.contactId   = null
        portalState.contactName = null
        portalState.permissions = {}
        portalState.accessToken = null
    }

    // Returns an axios config object with the portal token in the Authorization header.
    // Use this for all API calls made from portal views instead of modifying global defaults.
    const portalAxiosConfig = () => ({
        headers: { Authorization: `Bearer ${portalState.accessToken}` }
    })

    const setupPasscode = async (token, passcode) => {
        try {
            const res = await axios.post('/portal/setPasscode', { token, passcode })
            return res.data
        } catch (err) {
            return { error: err.response?.data?.error || { name: 'error.unknown' } }
        }
    }

    return {
        resolvePortal,
        authenticatePortal,
        restorePortalSession,
        logoutPortal,
        portalAxiosConfig,
        setupPasscode
    }
}

export default usePortal
